import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PiX } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/shadcn/ui/input';

const EMPTY_META = {};
const DEFAULT_DEBOUNCE_MS = 400;

function resolvePlaceholder(column, meta) {
  if (meta.filterPlaceholder) {
    return meta.filterPlaceholder;
  }

  const header = column.columnDef.header;
  if (typeof header === 'string' && header) {
    return `搜尋${header}`;
  }

  return '搜尋';
}

function ColumnFilterTextInput({ column }) {
  const meta = column.columnDef.meta ?? EMPTY_META;
  const isServerMode = meta.filterMode === 'server';
  const debounceMs = meta.filterDebounceMs ?? DEFAULT_DEBOUNCE_MS;
  const filterValue = isServerMode ? meta.filterValue : column.getFilterValue();
  const placeholder = resolvePlaceholder(column, meta);
  const externalValue = filterValue ?? '';
  const [localValue, setLocalValue] = useState(externalValue);
  const [prevExternalValue, setPrevExternalValue] = useState(externalValue);

  if (externalValue !== prevExternalValue) {
    setPrevExternalValue(externalValue);
    setLocalValue(externalValue);
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = localValue.trim();
      const nextValue = trimmed || undefined;
      const currentValue = externalValue || undefined;

      if (nextValue === currentValue) {
        return;
      }

      if (isServerMode) {
        meta.onServerFilterChange?.(nextValue);
        return;
      }

      column.setFilterValue(nextValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, debounceMs, externalValue, isServerMode, meta, column]);

  function handleClear(event) {
    event.stopPropagation();
    setLocalValue('');
  }

  return (
    <div className="relative w-full">
      <Input
        type="text"
        value={localValue}
        placeholder={placeholder}
        className={cn('h-7 text-xs')}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        onChange={(event) => setLocalValue(event.target.value)}
      />
      {localValue ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-0.5 size-6 -translate-y-1/2 text-muted-foreground"
          onClick={handleClear}
          onKeyDown={(event) => event.stopPropagation()}
          aria-label="清除篩選"
        >
          <PiX className="size-3.5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

ColumnFilterTextInput.propTypes = {
  column: PropTypes.shape({
    columnDef: PropTypes.shape({
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      meta: PropTypes.object,
    }),
    getFilterValue: PropTypes.func,
    setFilterValue: PropTypes.func,
  }).isRequired,
};

export default ColumnFilterTextInput;
